import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  Text,
  tokens,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '480px',
  },
  deleteList: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    margin: `${tokens.spacingVerticalS} 0`,
    paddingLeft: tokens.spacingHorizontalL,
  },
  deleteButton: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    borderTopColor: tokens.colorPaletteRedBackground3,
    borderRightColor: tokens.colorPaletteRedBackground3,
    borderBottomColor: tokens.colorPaletteRedBackground3,
    borderLeftColor: tokens.colorPaletteRedBackground3,
  },
});

export type DeleteConfirmDialogProps = {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog is closed (cancel or backdrop click) */
  onClose: () => void;
  /** Callback when the delete action is confirmed */
  onConfirm: () => void;
  /** The type of item being deleted (e.g., "workflow", "application") */
  itemType: string;
  /** Names of items to delete */
  itemNames: string[];
  /** Whether the delete action is in progress */
  isDeleting?: boolean;
};

/**
 * A reusable delete confirmation dialog that supports single or multiple items.
 */
export const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  itemType,
  itemNames,
  isDeleting = false,
}: DeleteConfirmDialogProps) => {
  const styles = useStyles();
  const count = itemNames.length;
  const plural = count !== 1;
  const itemLabel = plural ? `${itemType}s` : itemType;

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>
            Delete {plural ? `${count} ${itemLabel}` : itemLabel}?
          </DialogTitle>
          <DialogContent>
            <Text>
              {plural
                ? `Are you sure you want to delete the following ${itemLabel}? This action cannot be undone.`
                : `Are you sure you want to delete "${itemNames[0]}"? This action cannot be undone.`}
            </Text>
            {plural && (
              <ul className={styles.deleteList}>
                {itemNames.map((name) => <li key={name}>{name}</li>)}
              </ul>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={onConfirm}
              className={styles.deleteButton}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
